import {
  Box,
  Button,
  HStack,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { IconButton } from '@gamma/ui/buttons';
import DeleteSharpIcon from '@gamma/ui/icons/DeleteSharpIcon';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useGetIdpDomains } from '@investigator/features/admin/queries/useGetIdpDomains';
import { i18n } from '@investigator/shared/localization';
import AddCircleSharpIcon from '@gamma/ui/icons/AddCircleSharpIcon';
// TODO: Udpated to use package icons. Uncomment if needed in this component
// import AlternateEmailSharpIcon from '@gamma/ui/icons/AlternateEmailSharpIcon';
import { AddDomainModal } from './AddDomainModal';
import { DeleteDomainModal } from './DeleteDomainModal';
import { useState, useMemo, useCallback } from 'react';
import { MAX_SSO_DOMAINS } from './constants';
import { DataTable } from '@gamma/ui/data-table';
import { Column } from 'react-table';
import { NetworkStatus } from '@apollo/client';

const domainsText = i18n.pages.access.domainsList;

interface DomainRow {
  domain: string;
  actions: string;
  isDeleteDisabled: boolean;
}

export const DomainsList = () => {
  const { data, loading, error, networkStatus } = useGetIdpDomains({
    notifyOnNetworkStatusChange: true,
  });

  const isLoading = loading || networkStatus === NetworkStatus.refetch;

  const domains = useMemo(
    () => data?.getIdpDomains?.domains || [],
    [data?.getIdpDomains],
  );

  const {
    isOpen: addDomainIsOpen,
    onOpen: addDomainOnOpen,
    onClose: addDomainOnClose,
  } = useDisclosure();

  const {
    isOpen: deleteDomainIsOpen,
    onOpen: deleteDomainOnOpen,
    onClose: deleteDomainOnClose,
  } = useDisclosure();

  const [selectedDomain, setSelectedDomain] = useState<string>('');

  const isAddButtonDisabled = domains.length >= MAX_SSO_DOMAINS || isLoading;

  const handleDeleteClick = useCallback(
    (domain: string) => {
      setSelectedDomain(domain);
      deleteDomainOnOpen();
    },
    [deleteDomainOnOpen],
  );

  interface DomainsDataMapFn {
    (domain: string): DomainRow;
  }

  const domainsData: DomainRow[] = useMemo(
    (): DomainRow[] =>
      domains.map<DomainRow>(((domain: string) => ({
        domain,
        actions: domain,
        isDeleteDisabled: domains.length === 1,
      })) as DomainsDataMapFn),
    [domains],
  );

  const dataTableColumns: Column<DomainRow>[] = useMemo(
    () => [
      {
        Header: domainsText.domain,
        accessor: 'domain',
        disableSortBy: true,
        Cell: (props) => (
          <Text data-testid={`domain-name-${props.value}`}>{props.value}</Text>
        ),
      },
      {
        Header: '',
        accessor: 'actions',
        align: 'right',
        width: 80,
        disableSortBy: true,
        Cell: (props) => {
          const rowData = props.row.original;
          return (
            <Tooltip
              label={domainsText.deleteLastDomain}
              isDisabled={!rowData.isDeleteDisabled}
            >
              <IconButton
                icon={<DeleteSharpIcon color={'text.primary'} />}
                aria-label="Delete domain"
                variant="ghost"
                size="sm"
                isDisabled={rowData.isDeleteDisabled}
                data-testid={`remove-domain-button-${props.value}`}
                onClick={() => handleDeleteClick(props.value)}
              />
            </Tooltip>
          );
        },
      },
    ],
    [handleDeleteClick],
  );

  return isLoading || error ? (
    <GraphQLReqStatus loading={isLoading} error={error} />
  ) : (
    <VStack
      spacing={2}
      align="stretch"
      padding={2}
      backgroundColor={'layer.1'}
      borderRadius="md"
    >
      <HStack justify="space-between" align="center">
        <HStack spacing={2}>
          {/* TODO: add when icon is available */}
          {/* <AlternateEmailSharpIcon color={'text.primary'} /> */}
          <Text fontWeight="bold" data-testid="domains-list-header">
            @ {domainsText.approvedEmailDomains}
          </Text>
        </HStack>
        <Tooltip
          label={domainsText.disabledTooltip}
          isDisabled={!isAddButtonDisabled}
        >
          <Button
            aria-label="Add domain"
            variant="solid"
            colorScheme="gray"
            size="sm"
            leftIcon={<AddCircleSharpIcon />}
            data-testid="add-domain-button"
            onClick={addDomainOnOpen}
            isDisabled={isAddButtonDisabled}
          >
            {domainsText.addDomain}
          </Button>
        </Tooltip>
      </HStack>
      <DataTable
        key={`domains-table-${domains.length}`}
        data={domainsData}
        columns={dataTableColumns}
        isPaginable={true}
        isLined={true}
        hideColumnHeaders={true}
        autoResetPage={true}
        rowWrapper={({ children }) => (
          <Box
            bg="layer.2"
            borderRadius="md"
            p={2}
            my={1}
            sx={{
              '& > div:hover': {
                bg: 'layer.2 !important',
              },
              '& > div > div:hover': {
                bg: 'layer.2 !important',
              },
            }}
          >
            {children}
          </Box>
        )}
      />

      {/* Add Domain Modal */}
      <AddDomainModal
        isOpen={addDomainIsOpen}
        onClose={addDomainOnClose}
        existingDomains={domains}
      />
      {/* Delete Domain Modal */}
      <DeleteDomainModal
        isOpen={deleteDomainIsOpen}
        onClose={deleteDomainOnClose}
        domain={selectedDomain}
      />
    </VStack>
  );
};
